/**
 * Regelversion 12: „Euer Weg“. Zeitleiste, wann welche Werkzeuge dazukommen: im Lauf der ersten Legislatur,
 * ab der zweiten Legislatur und mit der nächsten Erfahrungsstufe. Reine Oberfläche, nur im Einzelspieler.
 */
import { useS } from '../../store/game';
import { usePref } from '../../lib/pref';
import { Panel } from '../../components/ui';
import { since } from '../../engine/version';
import { LV_MIN, lvlOf, levelName } from '../../engine/level12';
import { coalSeats } from '../../engine/election';
import { NEED } from '../../content/consts';
import { UNLOCKS, LATER, isUnlocked, progressiveOf, unlockText } from './unlock';
import { useTutorial } from './store';

/** `need`: Stufe, die dafür nötig ist (sonst 0). */
export type Step = { id: string; t: string; tab?: string; ok: boolean; when: string; rank: number; need: number };
/** Werkzeuge, die nur an der Erfahrungsstufe hängen. */
const LV_ONLY = [
  { id: 'budget', t: 'Haushalt selbst aufstellen', tab: 'haushalt' },
  { id: 'pre', t: 'Vorwahlkampf mit TV-Duellen' },
  { id: 'don', t: 'Parteispenden' },
];
/** Kurzname für die Gruppe einer Stufe. */
const SHORT: Record<string, string> = { presse: 'Kommunikation', agenda: 'Meinungsthemen', budget: 'eigener Haushalt', pre: 'Vorwahlkampf', don: 'Spenden', verwaltung: 'Verwaltung', ordnung: 'Wirtschaftsordnung', macht: 'Machtfragen' };
const LV_EXTRA: Record<number, string> = { 3: 'Gerichte, Verbände und Parteiflügel', 4: 'große Steuerreform, Verwaltung im Detail, alle Einflüsse stärker' };

export function roadmapSteps(S: any, prog: boolean): Step[] {
  const lv = lvlOf(S), rel = S.m - S.m0, out: Step[] = [];
  const free = (x: { id: string; t: string; tab?: string }): Step => ({ ...x, ok: true, when: 'frei', rank: 0, need: 0 });
  const lvStep = (x: { id: string; t: string }, need: number): Step => ({ ...x, ok: false, when: `ab Stufe ${need}`, rank: 1000 + need, need });
  for (const u0 of UNLOCKS) {
    const u = { ...u0, ...unlockText(S, u0) }, need = LV_MIN[u.id] ?? 1;
    if (isUnlocked(S, u.id, prog)) out.push(free(u));
    else if (need > lv) out.push(lvStep(u, need));
    else { const d = u.m - rel; out.push({ id: u.id, t: u.t, ok: false, when: d <= 1 ? 'nächsten Monat' : `in ${d} Monaten`, rank: d, need: 0 }); }
  }
  for (const x of LV_ONLY) { const need = LV_MIN[x.id] ?? 1; out.push(lv >= need ? free(x) : lvStep(x, need)); }
  const second = (S.career?.length ?? 0) > 0;
  for (const u of LATER) {
    const need = LV_MIN[u.id] ?? 1;
    if (isUnlocked(S, u.id, prog)) out.push(free(u));
    else if (need > lv) out.push(lvStep(u, need));
    else if (!second) out.push({ id: u.id, t: u.t, ok: false, when: 'ab der 2. Legislatur', rank: 500, need: 0 });
    else if (u.id === 'macht' && coalSeats() < NEED) out.push({ id: u.id, t: u.t, ok: false, when: 'nur mit eigener Mehrheit', rank: 600, need: 0 });
    else out.push(free(u));
  }
  return out.sort((a, b) => a.rank - b.rank);
}

export function Roadmap() {
  const S: any = useS(); const { off, seen } = useTutorial();
  const [open, setOpen] = usePref('kabinett-weg-offen', true);
  if (!S || S.mp || !since(12) || S.phase !== 'govern' || S.editing) return null;
  const steps = roadmapSteps(S, progressiveOf(S, off, seen)), lv = lvlOf(S);
  const done = steps.filter((x) => x.ok), soon = steps.filter((x) => !x.ok && !x.need);
  if (done.length === steps.length && lv >= 4) return null;
  const groups = [2, 3, 4].filter((l) => l > lv).map((l) => ({ l, tools: steps.filter((x) => x.need === l).map((x) => SHORT[x.id] || x.t) }));
  const title = <button type="button" className="ptoggle" aria-expanded={open} onClick={() => setOpen(!open)}>Euer Weg</button>;
  const aside = `${done.length} von ${steps.length} Werkzeugen`;
  const bar = <div className="wegbar" role="img" aria-label={aside}><i style={{ width: `${Math.round(done.length / steps.length * 100)}%` }} /></div>;
  if (!open) return <Panel title={title} aside={aside} className="weg closed">{bar}</Panel>;
  return (
    <Panel title={title} aside={aside} className="weg">
      {bar}
      <ol className="weglist">
        {soon.map((x, i) => <li key={x.id} className={i === 0 ? 'next' : ''}><b>{x.t}</b><span>{x.when}</span></li>)}
        {groups.map((g, i) => {
          const tools = [...g.tools, ...(LV_EXTRA[g.l] ? [LV_EXTRA[g.l]] : [])];
          return <li key={g.l} className={'lvl' + (!soon.length && !i ? ' next' : '')}>
            <b>Stufe {g.l}: {levelName(g.l)}</b>
            <span>{i === 0 ? 'Aufstieg nach einer gewonnenen Wahl' : 'nach einem weiteren Aufstieg'}{tools.length ? `. Neu: ${tools.join(', ')}` : ''}</span>
          </li>;
        })}
      </ol>
    </Panel>
  );
}
